import React from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Calendar, CheckCircle, FileText, Globe, GraduationCap, Mail, Phone, User, X } from 'lucide-react';

export function EducationComparison({ check, verificationData, setVerificationData, handleFieldVerification }) {
    const candidateData = {
      ...(check?.subcheck_data || {}),
      father_name: check?.father_name || check?.subcheck_data?.father_name || '',
      dob: check?.dob || check?.subcheck_data?.dob || check?.subcheck_data?.date_of_birth || '',
      candidate_name: check?.subcheck_data?.candidate_name || check?.subcheck_data?.name || check?.employee_name || '',
    };
    
    // Verification status options with color codes (same as Employment form)
    const verificationStatusOptions = [
      { value: 'verified', label: 'Verified', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50 border-green-300' },
      { value: 'discrepancy', label: 'Discrepancy', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50 border-red-300' },
      { value: 'partially_verified', label: 'Partially Verified', color: 'bg-sky-400', textColor: 'text-sky-700', bgLight: 'bg-sky-50 border-sky-300' },
      { value: 'not_verified', label: 'Not Verified', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50 border-amber-300' },
    ];
    
    // Get status color classes
    const getStatusClasses = (status) => {
      const found = verificationStatusOptions.find(o => o.value === status);
      return found || { color: 'bg-gray-300', textColor: 'text-gray-600', bgLight: 'bg-white border-gray-200' };
    };

    const educationTypes = [
      { value: '10th', label: '10th / SSC / Matriculation' },
      { value: '12th', label: '12th / HSC / Intermediate' },
      { value: 'diploma', label: 'Diploma' },
      { value: 'graduation', label: 'Graduation / Bachelor\'s' },
      { value: 'post_graduation', label: 'Post Graduation / Master\'s' },
      { value: 'doctorate', label: 'Doctorate / PhD' },
      { value: 'professional', label: 'Professional Certification' },
    ];
    
    // Clear form function for Education
    const clearEducationForm = () => {
      setVerificationData(prev => ({
        ...prev,
        verified_details: {
          ...prev.verified_details,
          candidate_name: '',
          name_status: '',
          education_type: '',
          education_type_status: '',
          course: '',
          course_status: '',
          specialization: '',
          specialization_status: '',
          year_of_passing: '',
          year_status: '',
          grade: '',
          grade_status: '',
          university_name: '',
          university_status: '',
          college_name: '',
          college_status: '',
          roll_number: '',
          roll_no_status: '',
          degree_certificate_status: '',
          degree_certificate_remarks: '',
          transcript_status: '',
          transcript_remarks: '',
          provisional_status: '',
          provisional_remarks: '',
          registrar_name: '',
          registrar_email: '',
          registrar_contact: '',
          education_verification_method: '',
          accreditation_status: '',
          education_verification_date: '',
        }
      }));
      toast.info('Form cleared');
    };
    
    return (
      <div className="space-y-4">
        {/* Header with Clear Button */}
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-700 text-white px-4 py-3 rounded-lg shadow-lg">
          <h4 className="font-semibold text-base flex items-center gap-2">
            <GraduationCap className="w-5 h-5" />
            Education Verification - {check?.subcheck_type || candidateData.education_type || 'Education'}
          </h4>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={clearEducationForm}
            className="bg-white/10 hover:bg-white/20 text-white border-white/30 hover:border-white/50"
          >
            <X className="w-4 h-4 mr-1" />
            Clear Form
          </Button>
        </div>
        
        {/* Status Color Legend */}
        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg border">
          <span className="text-xs font-medium text-slate-600">Status Legend:</span>
          <div className="flex items-center gap-3">
            {verificationStatusOptions.map(opt => (
              <div key={opt.value} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${opt.color}`}></div>
                <span className={`text-xs ${opt.textColor}`}>{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Main Comparison Grid */}
        <div className="grid grid-cols-2 gap-4">
          {/* Candidate Provided (Left) */}
          <Card className="border-2 border-blue-200 shadow-md bg-gradient-to-b from-blue-50 to-white">
            <CardHeader className="pb-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-lg">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="w-4 h-4" />
                Candidate Provided
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm pt-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Candidate Name</span>
                  <span className="font-semibold text-slate-800">{candidateData.candidate_name || candidateData.name || check?.employee_name || '-'}</span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Education Type</span>
                  <span className="font-semibold text-slate-800">{candidateData.education_type || candidateData.degree_type || '-'}</span>
                </div>
              </div>
              
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">Course / Degree</span>
                <span className="font-semibold text-slate-800">{candidateData.course || candidateData.degree || candidateData.qualification || '-'}</span>
              </div>
              
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">Specialization / Branch</span>
                <span className="font-semibold text-slate-800">{candidateData.specialization || candidateData.branch || candidateData.major || '-'}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Year of Passing</span>
                  <span className="font-semibold text-slate-800">{candidateData.year_of_passing || candidateData.passing_year || '-'}</span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Grade / CGPA / %</span>
                  <span className="font-semibold text-slate-800">{candidateData.grade || candidateData.cgpa || candidateData.percentage || '-'}</span>
                </div>
              </div>
              
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">University Name</span>
                <span className="font-semibold text-slate-800">{candidateData.university_name || candidateData.university || '-'}</span>
              </div>
              
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">Institution / College Name</span>
                <span className="font-semibold text-slate-800">{candidateData.college || candidateData.institution_name || candidateData.college_name || '-'}</span>
              </div>
              
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">Roll No / Registration No</span>
                <span className="font-semibold text-slate-800">{candidateData.roll_number || candidateData.registration_number || candidateData.reg_no || '-'}</span>
              </div>
            </CardContent>
          </Card>
          
          {/* Verified Details (Right) */}
          <Card className="border-2 border-green-200 shadow-md bg-gradient-to-b from-green-50 to-white">
            <CardHeader className="pb-2 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-t-lg">
              <CardTitle className="text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Verified Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              {/* Candidate Name with Color */}
              <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.name_status).bgLight}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-slate-700">Candidate Name</Label>
                  <Select 
                    value={verificationData.verified_details.name_status || ''} 
                    onValueChange={(v) => handleFieldVerification('name_status', true, v)}
                  >
                    <SelectTrigger className="h-7 w-36 text-xs border-slate-300">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                            {o.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={verificationData.verified_details.candidate_name || ''} 
                  onChange={(e) => handleFieldVerification('candidate_name', true, e.target.value)}
                  className="h-8 text-sm border-slate-200"
                  placeholder="Verified name"
                />
              </div>
              
              {/* Course with Color */}
              <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.course_status).bgLight}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-slate-700">Course / Degree</Label>
                  <Select 
                    value={verificationData.verified_details.course_status || ''} 
                    onValueChange={(v) => handleFieldVerification('course_status', true, v)}
                  >
                    <SelectTrigger className="h-7 w-36 text-xs border-slate-300">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                            {o.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={verificationData.verified_details.course || ''} 
                  onChange={(e) => handleFieldVerification('course', true, e.target.value)}
                  className="h-8 text-sm border-slate-200"
                  placeholder="e.g., B.Tech, MBA, BSc"
                />
              </div>
              
              {/* Specialization with Color */}
              <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.specialization_status).bgLight}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-slate-700">Specialization / Branch</Label>
                  <Select 
                    value={verificationData.verified_details.specialization_status || ''} 
                    onValueChange={(v) => handleFieldVerification('specialization_status', true, v)}
                  >
                    <SelectTrigger className="h-7 w-36 text-xs border-slate-300">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                            {o.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={verificationData.verified_details.specialization || ''} 
                  onChange={(e) => handleFieldVerification('specialization', true, e.target.value)}
                  className="h-8 text-sm border-slate-200"
                  placeholder="e.g., Computer Science, Finance"
                />
              </div>
              
              {/* Year of Passing & Grade */}
              <div className="grid grid-cols-2 gap-2">
                <div className={`p-2 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.year_status).bgLight}`}>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-semibold text-slate-700">Year</Label>
                    <Select 
                      value={verificationData.verified_details.year_status || ''} 
                      onValueChange={(v) => handleFieldVerification('year_status', true, v)}
                    >
                      <SelectTrigger className="h-6 w-24 text-xs border-slate-300"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        {verificationStatusOptions.map(o => (
                          <SelectItem key={o.value} value={o.value}>
                            <div className="flex items-center gap-1">
                              <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                              {o.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input 
                    value={verificationData.verified_details.year_of_passing || ''} 
                    onChange={(e) => handleFieldVerification('year_of_passing', true, e.target.value)}
                    className="h-7 text-sm border-slate-200"
                    placeholder="YYYY"
                  />
                </div>
                <div className={`p-2 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.grade_status).bgLight}`}>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-semibold text-slate-700">Grade</Label>
                    <Select 
                      value={verificationData.verified_details.grade_status || ''} 
                      onValueChange={(v) => handleFieldVerification('grade_status', true, v)}
                    >
                      <SelectTrigger className="h-6 w-24 text-xs border-slate-300"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        {verificationStatusOptions.map(o => (
                          <SelectItem key={o.value} value={o.value}>
                            <div className="flex items-center gap-1">
                              <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                              {o.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input 
                    value={verificationData.verified_details.grade || ''} 
                    onChange={(e) => handleFieldVerification('grade', true, e.target.value)}
                    className="h-7 text-sm border-slate-200"
                    placeholder="CGPA/%"
                  />
                </div>
              </div>
              
              {/* University with Color */}
              <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.university_status).bgLight}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-slate-700">University Name</Label>
                  <Select 
                    value={verificationData.verified_details.university_status || ''} 
                    onValueChange={(v) => handleFieldVerification('university_status', true, v)}
                  >
                    <SelectTrigger className="h-7 w-36 text-xs border-slate-300">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                            {o.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={verificationData.verified_details.university_name || ''} 
                  onChange={(e) => handleFieldVerification('university_name', true, e.target.value)}
                  className="h-8 text-sm border-slate-200"
                  placeholder="University name"
                />
              </div>
              
              {/* College with Color */}
              <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.college_status).bgLight}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-slate-700">Institution / College</Label>
                  <Select 
                    value={verificationData.verified_details.college_status || ''} 
                    onValueChange={(v) => handleFieldVerification('college_status', true, v)}
                  >
                    <SelectTrigger className="h-7 w-36 text-xs border-slate-300">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                            {o.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={verificationData.verified_details.college_name || ''} 
                  onChange={(e) => handleFieldVerification('college_name', true, e.target.value)}
                  className="h-8 text-sm border-slate-200"
                  placeholder="College/Institution name"
                />
              </div>
              
              {/* Roll No with Color */}
              <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.roll_no_status).bgLight}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-slate-700">Roll No / Registration</Label>
                  <Select 
                    value={verificationData.verified_details.roll_no_status || ''} 
                    onValueChange={(v) => handleFieldVerification('roll_no_status', true, v)}
                  >
                    <SelectTrigger className="h-7 w-36 text-xs border-slate-300">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                            {o.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={verificationData.verified_details.roll_number || ''} 
                  onChange={(e) => handleFieldVerification('roll_number', true, e.target.value)}
                  className="h-8 text-sm border-slate-200"
                  placeholder="Roll/Registration number"
                />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Document Verification Section */}
        <Card className="border-2 border-purple-200 shadow-md">
          <CardHeader className="pb-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-t-lg">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Document Verification
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-4 pt-4 bg-gradient-to-b from-purple-50 to-white">
            {/* Degree Certificate */}
            <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${
              verificationData.verified_details.degree_certificate_status === 'verified_authentic' 
                ? 'bg-green-50 border-green-200' 
                : verificationData.verified_details.degree_certificate_status === 'suspicious'
                  ? 'bg-red-50 border-red-200'
                  : 'bg-white border-purple-100'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <FileText className="w-3 h-3 text-purple-500" />Degree/Marksheet
                </Label>
                <Select 
                  value={verificationData.verified_details.degree_certificate_status || ''} 
                  onValueChange={(v) => handleFieldVerification('degree_certificate_status', true, v)}
                >
                  <SelectTrigger className="h-7 w-32 text-xs border-purple-200"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verified_authentic"><span className="text-green-600 font-medium">✓ Verified - Authentic</span></SelectItem>
                    <SelectItem value="verified_copy"><span className="text-green-600">Verified - Copy</span></SelectItem>
                    <SelectItem value="not_provided"><span className="text-amber-600">Not Provided</span></SelectItem>
                    <SelectItem value="suspicious"><span className="text-red-600 font-medium">✗ Suspicious/Fake</span></SelectItem>
                    <SelectItem value="pending"><span className="text-slate-500">Pending</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea 
                value={verificationData.verified_details.degree_certificate_remarks || ''} 
                onChange={(e) => handleFieldVerification('degree_certificate_remarks', true, e.target.value)}
                placeholder="Document verification remarks..."
                className="h-12 text-xs resize-none border-slate-200"
              />
            </div>
            
            {/* Transcript */}
            <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${
              verificationData.verified_details.transcript_status === 'verified_authentic' 
                ? 'bg-green-50 border-green-200' 
                : verificationData.verified_details.transcript_status === 'suspicious'
                  ? 'bg-red-50 border-red-200'
                  : 'bg-white border-purple-100'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <FileText className="w-3 h-3 text-purple-500" />Transcript/DMC
                </Label>
                <Select 
                  value={verificationData.verified_details.transcript_status || ''} 
                  onValueChange={(v) => handleFieldVerification('transcript_status', true, v)}
                >
                  <SelectTrigger className="h-7 w-32 text-xs border-purple-200"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verified_authentic"><span className="text-green-600 font-medium">✓ Verified - Authentic</span></SelectItem>
                    <SelectItem value="verified_copy"><span className="text-green-600">Verified - Copy</span></SelectItem>
                    <SelectItem value="not_provided"><span className="text-amber-600">Not Provided</span></SelectItem>
                    <SelectItem value="suspicious"><span className="text-red-600 font-medium">✗ Suspicious/Fake</span></SelectItem>
                    <SelectItem value="pending"><span className="text-slate-500">Pending</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea 
                value={verificationData.verified_details.transcript_remarks || ''} 
                onChange={(e) => handleFieldVerification('transcript_remarks', true, e.target.value)}
                placeholder="Transcript remarks..."
                className="h-12 text-xs resize-none border-slate-200"
              />
            </div>
            
            {/* Provisional Certificate */}
            <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${
              verificationData.verified_details.provisional_status === 'verified_authentic' 
                ? 'bg-green-50 border-green-200' 
                : verificationData.verified_details.provisional_status === 'suspicious'
                  ? 'bg-red-50 border-red-200'
                  : 'bg-white border-purple-100'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <FileText className="w-3 h-3 text-purple-500" />Provisional/Migration
                </Label>
                <Select 
                  value={verificationData.verified_details.provisional_status || ''} 
                  onValueChange={(v) => handleFieldVerification('provisional_status', true, v)}
                >
                  <SelectTrigger className="h-7 w-32 text-xs border-purple-200"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verified_authentic"><span className="text-green-600 font-medium">✓ Verified - Authentic</span></SelectItem>
                    <SelectItem value="verified_copy"><span className="text-green-600">Verified - Copy</span></SelectItem>
                    <SelectItem value="not_provided"><span className="text-amber-600">Not Provided</span></SelectItem>
                    <SelectItem value="not_applicable"><span className="text-slate-500">Not Applicable</span></SelectItem>
                    <SelectItem value="pending"><span className="text-slate-500">Pending</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea 
                value={verificationData.verified_details.provisional_remarks || ''} 
                onChange={(e) => handleFieldVerification('provisional_remarks', true, e.target.value)}
                placeholder="Provisional/Migration remarks..."
                className="h-12 text-xs resize-none border-slate-200"
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Registrar/University Verification Section */}
        <Card className="border-2 border-teal-200 shadow-md">
          <CardHeader className="pb-2 bg-gradient-to-r from-teal-600 to-teal-500 text-white rounded-t-lg">
            <CardTitle className="text-sm flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Registrar / University Verification
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 pt-4 bg-gradient-to-b from-teal-50 to-white">
            <div className="space-y-3">
              <div className="p-3 bg-white rounded-lg border-2 border-teal-100 shadow-sm">
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2">
                  <User className="w-3 h-3 text-teal-500" />Registrar/Verifier Name
                </Label>
                <Input 
                  value={verificationData.verified_details.registrar_name || ''} 
                  onChange={(e) => handleFieldVerification('registrar_name', true, e.target.value)}
                  className="h-8 text-sm border-teal-200"
                  placeholder="Name of university official"
                />
              </div>
              <div className="p-3 bg-white rounded-lg border-2 border-teal-100 shadow-sm">
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2">
                  <Mail className="w-3 h-3 text-teal-500" />Registrar Email
                </Label>
                <Input 
                  value={verificationData.verified_details.registrar_email || ''} 
                  onChange={(e) => handleFieldVerification('registrar_email', true, e.target.value)}
                  className="h-8 text-sm border-teal-200"
                  placeholder="Official email"
                />
              </div>
              <div className="p-3 bg-white rounded-lg border-2 border-teal-100 shadow-sm">
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2">
                  <Phone className="w-3 h-3 text-teal-500" />Registrar Contact
                </Label>
                <Input 
                  value={verificationData.verified_details.registrar_contact || ''} 
                  onChange={(e) => handleFieldVerification('registrar_contact', true, e.target.value)}
                  className="h-8 text-sm border-teal-200"
                  placeholder="Phone number"
                />
              </div>
            </div>
            <div className="space-y-3">
              <div className="p-3 bg-white rounded-lg border-2 border-teal-100 shadow-sm">
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2">
                  <Globe className="w-3 h-3 text-teal-500" />Verification Method
                </Label>
                <Select 
                  value={verificationData.verified_details.education_verification_method || ''} 
                  onValueChange={(v) => handleFieldVerification('education_verification_method', true, v)}
                >
                  <SelectTrigger className="h-8 text-sm border-teal-200"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online_portal">University Online Portal</SelectItem>
                    <SelectItem value="email">Email to Registrar</SelectItem>
                    <SelectItem value="physical_visit">Physical Visit</SelectItem>
                    <SelectItem value="phone">Phone Verification</SelectItem>
                    <SelectItem value="third_party">Third Party Agency</SelectItem>
                    <SelectItem value="ugc_database">UGC Database</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${
                verificationData.verified_details.accreditation_status === 'ugc_recognized' || 
                verificationData.verified_details.accreditation_status === 'naac_accredited'
                  ? 'bg-green-50 border-green-200' 
                  : verificationData.verified_details.accreditation_status === 'not_recognized'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-white border-teal-100'
              }`}>
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2">
                  <CheckCircle className="w-3 h-3 text-teal-500" />University Accreditation
                </Label>
                <Select 
                  value={verificationData.verified_details.accreditation_status || ''} 
                  onValueChange={(v) => handleFieldVerification('accreditation_status', true, v)}
                >
                  <SelectTrigger className="h-8 text-sm border-slate-300"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ugc_recognized"><span className="text-green-600 font-medium">✓ UGC Recognized</span></SelectItem>
                    <SelectItem value="aicte_approved"><span className="text-green-600">AICTE Approved</span></SelectItem>
                    <SelectItem value="naac_accredited"><span className="text-green-600 font-medium">✓ NAAC Accredited</span></SelectItem>
                    <SelectItem value="state_university"><span className="text-blue-600">State University</span></SelectItem>
                    <SelectItem value="deemed_university"><span className="text-blue-600">Deemed University</span></SelectItem>
                    <SelectItem value="not_recognized"><span className="text-red-600 font-medium">✗ Not Recognized</span></SelectItem>
                    <SelectItem value="verification_pending"><span className="text-amber-600">Verification Pending</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="p-3 bg-white rounded-lg border-2 border-teal-100 shadow-sm">
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2">
                  <Calendar className="w-3 h-3 text-teal-500" />Verification Date
                </Label>
                <Input 
                  type="date"
                  value={verificationData.verified_details.education_verification_date || new Date().toISOString().split('T')[0]} 
                  onChange={(e) => handleFieldVerification('education_verification_date', true, e.target.value)}
                  className="h-8 text-sm border-teal-200"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
}
